import { useCallback, useEffect, useMemo, useState } from "react";
import { useHorizontalScrollRow } from "../../hooks/useHorizontalWheelScroll";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import dayjs from "dayjs";
import {
  ImpactAssessmentChecklistForm,
  assessmentChecklistToFormValue,
  checklistFormValueToCreatePayload,
  checklistFormValueToUpdatePayload,
  type ImpactAssessmentChecklistFormValue,
} from "../ImpactAssessmentChecklistForm";
import { useConceptCodeMapping } from "../../hooks/useConceptCodeMapping";
import { useGlobalConfig } from "../../hooks/useGlobalConfig";
import { useHasPrivilegeStatus } from "../../hooks/useHasPrivilege";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import {
  createAssessmentChecklist,
  deleteAssessmentChecklist,
  fetchAssessmentChecklistById,
  fetchAssessmentChecklists,
  updateAssessmentChecklist,
  type AssessmentChecklistItem,
} from "../../services/assessmentService";
import {
  getConceptsKeywordsTotal,
  isAssessmentApiError,
  transformChecklistToFilterBarItem,
} from "../RegulationsTable/FilterBar/filterBarUtils";
import { SelectPlanDialogIcon } from "./SelectPlanDialogIcon";
import pencilIcon from "../../assets/pencil.svg";
import trashIcon from "../../assets/Trash.svg";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";

const MAX_PLANS = 10;
const MAX_PLANS_MESSAGE =
  "Maximum number of plans (10) reached. Delete an existing plan to create a new one.";

// Per Figma spec for the Plan Management dialog. Centralised so the JSX below
// stays readable; do not move into the JSX inline unless you also want to
// re-derive the layout math (header height, content padding, card sizing).
/** Figma header: 81px total (padding included), 24px horizontal padding. */
const DIALOG_TITLE_ROW_SX = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  boxSizing: "border-box",
  height: 81,
  minHeight: 81,
  maxHeight: 81,
  m: 0,
  p: 0,
  px: "24px",
  py: "16px",
  flexShrink: 0,
  borderBottom: "1px solid #F2F2F2",
} as const;

const DIALOG_TITLE_TEXT_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 700,
  fontSize: 20,
  lineHeight: "25px",
  letterSpacing: 0,
  textTransform: "capitalize" as const,
  color: "#0D0D0D",
} as const;

const DIALOG_TITLE_DESC_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#595959",
} as const;

/** Figma tag: 28px tall (16px text + 5px vertical padding ×2 + 2px border). */
const RUN_FOR_REGULATIONS_BADGE_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  height: 28,
  px: "9px",
  bgcolor: "#F1F5F9",
  border: "1px solid #E2E8F0",
  borderRadius: "4px",
  flexShrink: 0,
  cursor: "default",
} as const;

const RUN_FOR_REGULATIONS_BADGE_TEXT_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 500,
  fontSize: 12,
  lineHeight: "16px",
  letterSpacing: 0,
  color: "#475569",
  whiteSpace: "nowrap",
  m: 0,
  p: 0,
} as const;

function formatRunForRegulationsLabel(count: number): string {
  return `Run for ${count} regulations`;
}

/** Figma content inset: 32px sides/bottom; header border → cards is 24px (on card row, not content pt). */
const DIALOG_CONTENT_SX = {
  boxSizing: "border-box",
  padding: "0 32px 54px 32px",
  display: "flex",
  flexDirection: "column",
  gap: "28px",
  overflow: "visible",
  "&.MuiDialogContent-root": {
    paddingTop: 0,
    paddingRight: "32px",
    paddingBottom: "54px",
    paddingLeft: "32px",
  },
} as const;

/** Figma 291:11179 — distance from header bottom border to top of plan cards. */
const CARDS_SECTION_TOP_OFFSET_SX = {
  mt: "24px",
  flexShrink: 0,
} as const;

/**
 * Card row layout. The row height is applied separately because it must grow
 * by 12px once a horizontal scrollbar appears, otherwise the scrollbar eats
 * into the bottom of the cards. See `getCardsRowHeight`.
 */
const CARDS_ROW_SCROLLBAR_VISIBLE_SX = {
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { height: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#D1D5DB",
    borderRadius: 3,
  },
} as const;

/** Scrollbar visible on hover or while drag-panning; hidden otherwise. */
const CARDS_ROW_SX = {
  display: "flex",
  alignItems: "flex-start",
  gap: "28px",
  flexShrink: 0,
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { height: 0 },
  "&:hover": CARDS_ROW_SCROLLBAR_VISIBLE_SX,
  "&.is-dragging": CARDS_ROW_SCROLLBAR_VISIBLE_SX,
} as const;

const CARDS_ROW_BASE_HEIGHT = 189;
const CARDS_ROW_SCROLLBAR_HEIGHT_OFFSET = 12;

/**
 * AddNew + 3 plans (= 4 cards) is the first count that overflows the 1136px
 * inner content width, given 320px cards and 28px gaps. Once that happens we
 * pad the row by 12px so the horizontal scrollbar sits below the cards
 * instead of clipping them.
 */
function getCardsRowHeight(planCount: number): number {
  return planCount > 2
    ? CARDS_ROW_BASE_HEIGHT + CARDS_ROW_SCROLLBAR_HEIGHT_OFFSET
    : CARDS_ROW_BASE_HEIGHT;
}

/** Figma: 320×189 incl. 2px dashed border — same box model as plan cards. */
const ADD_NEW_CARD_SX = {
  boxSizing: "border-box",
  width: 320,
  height: 189,
  flexShrink: 0,
  borderRadius: "8px",
  border: "2px dashed #E2E8F0",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  cursor: "pointer",
  color: "#595959",
  backgroundColor: "transparent",
  textTransform: "none" as const,
  transition: "border-color .15s",
  "&:hover": {
    borderColor: "primary.main",
    color: "primary.main",
    backgroundColor: "transparent",
  },
} as const;

const PLAN_CARD_BASE_SX = {
  width: 320,
  height: 189,
  minWidth: 230,
  minHeight: 165,
  flexShrink: 0,
  borderRadius: "8px",
  p: "20px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  cursor: "pointer",
  backgroundColor: "background.paper",
  boxSizing: "border-box" as const,
} as const;

const PLAN_CARD_NAME_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 700,
  fontSize: 18,
  lineHeight: "28px",
  color: "#0D0D0D",
  // Truncate so the name always fits row 1.
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  flex: 1,
  minWidth: 0,
} as const;

const PLAN_CARD_DESC_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  color: "#595959",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
} as const;

const PLAN_CARD_BADGE_SX = {
  height: 24,
  borderRadius: "9999px",
  px: "12px",
  py: "2px",
  backgroundColor: "#F3F4F6",
  color: "#595959",
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: "20px",
  "& .MuiChip-label": { px: 0 },
} as const;

const PLAN_DETAIL_VALUE_CHIP_SX = {
  maxWidth: "100%",
  width: "fit-content",
  height: 27,
  borderRadius: "12px",
  gap: "4px",
  px: 1,
  py: 0.5,
  backgroundColor: "#F2F6FA",
  color: "#101828",
  "& .MuiChip-label": {
    px: 0,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} as const;

const PLAN_CARD_DATE_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "20px",
  color: "#A6A6A6",
} as const;

const PLAN_CARD_ICON_BUTTON_SX = {
  p: 0.25,
  borderRadius: "4px",
  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
} as const;

/** Add / Edit plan nested dialog — Figma 244:10986 (slate borders, Nunito, primary actions). */
const PLAN_FORM_DIALOG_PAPER_SX = {
  borderRadius: "12px",
  maxWidth: 1080,
  width: "100%",
  maxHeight: "95vh",
  boxShadow:
    "0px 3px 6px -4px rgba(0,0,0,0.12), 0px 6px 16px 0px rgba(0,0,0,0.08), 0px 9px 28px 8px rgba(0,0,0,0.05)",
} as const;

const PLAN_FORM_CONTENT_SX = {
  pt: "32px",
  px: "32px",
  pb: "14px",
  display: "flex",
  flexDirection: "column",
  gap: 0,
  bgcolor: "#FFFFFF",
} as const;

const PLAN_FORM_ACTIONS_SX = {
  px: "32px",
  py: "12px",
  pt: "12px",
  borderTop: "1px solid #F3F4F6",
  bgcolor: "#FFFFFF",
  justifyContent: "flex-end",
  gap: "12px",
} as const;

const EMPTY_PLAN_FORM: ImpactAssessmentChecklistFormValue = {
  name: "",
  description: "",
  selectedConceptIds: [],
  selectedConceptsDetails: [],
  keywords: "",
};

type PlanFormMode = "create" | "edit";

const PLAN_FORM_HEADER_COPY: Record<
  PlanFormMode,
  { title: string; description: string }
> = {
  create: {
    title: "Add New Impact Assessment Plan",
    description:
      "Create plan details and define the assessment dimension scope.",
  },
  edit: {
    title: "Edit Impact Assessment Plan",
    description:
      "Edit plan details and define the assessment dimension scope.",
  },
};

function getPlanCounts(plan: AssessmentChecklistItem) {
  const transformed = transformChecklistToFilterBarItem(plan);
  return {
    concepts: transformed.conceptsCount,
    dimensions: transformed.dimensionsCount,
    keywords: transformed.keywordsCount,
  };
}

function getPlanConceptGroups(plan: AssessmentChecklistItem) {
  return plan.concepts?.concepts ?? [];
}

function formatPlanDate(plan: AssessmentChecklistItem): string {
  const raw = plan.updated_at || plan.created_at;
  if (!raw) return "—";
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : "—";
}

export type SelectImpactAssessmentPlanDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;

  /**
   * Optional footer override. When omitted, this dialog behaves like "plan management"
   * (Assessment Center) and does not show any footer actions.
   */
  footer?: (args: {
    selectedPlan: AssessmentChecklistItem | null;
    loading: boolean;
    canReadChecklist: boolean;
  }) => React.ReactNode;
  /** Regulation IDs included in this run; shows header badge with hover tooltip. */
  runRegulationIds?: string[];
}>;

export default function SelectImpactAssessmentPlanDialog({
  open,
  onClose,
  footer,
  runRegulationIds,
}: SelectImpactAssessmentPlanDialogProps) {
  const { showMessage } = useSnackbar();
  const { config } = useGlobalConfig();
  const maxTotalSelections = config?.max_checklist_concepts ?? 25;
  const maxKeywordLength = config?.max_keyword_length ?? 80;

  const { has: canReadChecklist, loaded: privilegesLoaded } =
    useHasPrivilegeStatus("gri.assessment.checklist");
  const { conceptCodeMapping } = useConceptCodeMapping(open && canReadChecklist);

  const [plans, setPlans] = useState<AssessmentChecklistItem[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<AssessmentChecklistItem | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<PlanFormMode>("create");
  const [editingPlan, setEditingPlan] =
    useState<AssessmentChecklistItem | null>(null);
  const [formValue, setFormValue] =
    useState<ImpactAssessmentChecklistFormValue>(EMPTY_PLAN_FORM);

  const cardsRowScrollEnabled =
    open && privilegesLoaded && canReadChecklist && !loading;
  const { ref: cardsRowRef, isDragging: isCardsRowDragging } =
    useHorizontalScrollRow<HTMLDivElement>({ enabled: cardsRowScrollEnabled });
  const cardsRowScrollable = plans.length > 2;

  const loadPlans = useCallback(async () => {
    if (!canReadChecklist) return;
    setLoading(true);
    try {
      const response = await fetchAssessmentChecklists();
      const sorted = [...(response.checklists ?? [])].sort(
        (a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0),
      );
      setPlans(sorted);
      setSelectedPlanId((current) => {
        if (current && sorted.some((plan) => plan.id === current)) {
          return current;
        }
        return sorted[0]?.id ?? null;
      });
    } catch (error) {
      setPlans([]);
      showMessage(
        error instanceof Error ? error.message : "Failed to load plans",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [canReadChecklist, showMessage]);

  useEffect(() => {
    if (open) {
      void loadPlans();
    }
  }, [open, loadPlans]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );

  const canSavePlan = useMemo(() => {
    const total = getConceptsKeywordsTotal(formValue);
    return formValue.name.trim() !== "" && total >= 1 && total <= maxTotalSelections;
  }, [formValue, maxTotalSelections]);

  const resetForm = useCallback(() => {
    setFormValue(EMPTY_PLAN_FORM);
    setEditingPlan(null);
    setFormMode("create");
  }, []);

  const handleOpenCreate = useCallback(() => {
    if (plans.length >= MAX_PLANS) {
      // Yellow warning toast per Figma — gated overflow, not a hard error.
      showMessage(MAX_PLANS_MESSAGE, "warning");
      return;
    }
    resetForm();
    setFormMode("create");
    setFormOpen(true);
  }, [plans.length, resetForm, showMessage]);

  const handleOpenEdit = useCallback((plan: AssessmentChecklistItem) => {
    setEditingPlan(plan);
    setFormMode("edit");
    setFormValue(assessmentChecklistToFormValue(plan));
    setFormOpen(true);
    fetchAssessmentChecklistById(plan.id).then((fullPlan) => {
      if (fullPlan) {
        setEditingPlan(fullPlan);
        setFormValue(assessmentChecklistToFormValue(fullPlan));
      }
    });
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormOpen(false);
    resetForm();
  }, [resetForm]);

  const hasDuplicateName = useCallback(
    (name: string) =>
      plans.some(
        (plan) =>
          plan.id !== editingPlan?.id &&
          plan.name.trim().toLowerCase() === name.trim().toLowerCase(),
      ),
    [editingPlan?.id, plans],
  );

  const handleSavePlan = useCallback(async () => {
    const name = formValue.name.trim();
    if (hasDuplicateName(name)) {
      showMessage(`A plan with name "${name}" already exists.`, "error");
      return;
    }

    setSaving(true);
    try {
      const response =
        formMode === "create"
          ? await createAssessmentChecklist(
              checklistFormValueToCreatePayload(formValue, conceptCodeMapping),
            )
          : await updateAssessmentChecklist(
              editingPlan?.id ?? 0,
              checklistFormValueToUpdatePayload(
                formValue,
                editingPlan?.is_favorite,
                conceptCodeMapping,
              ),
            );

      if (isAssessmentApiError(response)) {
        showMessage(response.message ?? "Failed to save plan.", "error");
        return;
      }

      showMessage(
        formMode === "create"
          ? "Plan created successfully."
          : "Plan updated successfully.",
        "success",
      );
      handleCloseForm();
      await loadPlans();
    } catch (error) {
      showMessage(
        error instanceof Error ? error.message : "Failed to save plan.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }, [
    conceptCodeMapping,
    editingPlan,
    formMode,
    formValue,
    handleCloseForm,
    hasDuplicateName,
    loadPlans,
    showMessage,
  ]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteAssessmentChecklist(deleteTarget.id);
      showMessage("Plan deleted successfully.", "success");
      setDeleteTarget(null);
      await loadPlans();
    } catch (error) {
      showMessage(
        error instanceof Error ? error.message : "Failed to delete plan.",
        "error",
      );
    }
  }, [deleteTarget, loadPlans, showMessage]);

  const handleTogglePlanFavorite = useCallback(
    async (plan: AssessmentChecklistItem) => {
      const newFavorite = !(plan.is_favorite ?? false);
      try {
        await updateAssessmentChecklist(plan.id, {
          name: plan.name,
          description: plan.description,
          concepts: { concepts: plan.concepts?.concepts ?? [] },
          keywords: plan.keywords ?? [],
          is_favorite: newFavorite,
        });
        showMessage(
          newFavorite ? "Added to favorites" : "Removed from favorites",
          "success",
        );
        await loadPlans();
      } catch (error) {
        showMessage(
          error instanceof Error ? error.message : "Failed to update favorite",
          "error",
        );
      }
    },
    [loadPlans, showMessage],
  );

  const renderSelectedPlanDetails = () => {
    if (!selectedPlan) {
      return null;
    }

    const conceptGroups = getPlanConceptGroups(selectedPlan);
    const keywords = selectedPlan.keywords ?? [];

    return (
      <Box sx={{ mt: 0, p: 0 }}>
        {conceptGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No values configured.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
              },
              columnGap: 4,
              rowGap: 2,
              alignItems: "start",
            }}
          >
            {conceptGroups.map((group) => (
              <Box key={`${group.concept_type}-${group.concept_code}`} sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    width: 480,
                    maxWidth: "100%",
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    color: "#3641538C",
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: "20px",
                  }}
                >
                  {`${group.concept_type} (${group.values?.length ?? 0})`}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
                  {(group.values ?? []).map((value) => (
                    <Tooltip
                      key={`${group.concept_code}-${value.code}`}
                      title={value.name}
                      placement="top"
                      enterDelay={400}
                    >
                      <Chip
                        size="small"
                        label={value.name}
                        sx={PLAN_DETAIL_VALUE_CHIP_SX}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {keywords.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{
                width: 480,
                maxWidth: "100%",
                height: 20,
                display: "flex",
                alignItems: "center",
                gap: "14px",
                color: "#3641538C",
                fontWeight: 600,
                mb: 1,
                lineHeight: "20px",
              }}
            >
              {`Keywords (${keywords.length})`}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
              {keywords.map((keyword) => (
                <Tooltip
                  key={keyword}
                  title={keyword}
                  placement="top"
                  enterDelay={400}
                >
                  <Chip
                    size="small"
                    label={keyword}
                    sx={PLAN_DETAIL_VALUE_CHIP_SX}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderPlanContent = () => {
    // Privileges are fetched once globally on app boot; on a cold refresh we
    // may render before that response arrives. Show a spinner instead of
    // falsely reporting "no permission" during that window.
    if (!privilegesLoaded) {
      return (
        <Box
          sx={{
            ...CARDS_SECTION_TOP_OFFSET_SX,
            display: "flex",
            justifyContent: "center",
            py: 6,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (!canReadChecklist) {
      return (
        <Typography
          color="text.secondary"
          sx={{ ...CARDS_SECTION_TOP_OFFSET_SX, py: 6, textAlign: "center" }}
        >
          You do not have permission to manage assessment plans.
        </Typography>
      );
    }

    if (loading) {
      return (
        <Box
          sx={{
            ...CARDS_SECTION_TOP_OFFSET_SX,
            display: "flex",
            justifyContent: "center",
            py: 6,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      );
    }

    return (
      <>
        <Box
          ref={cardsRowRef}
          className={isCardsRowDragging ? "is-dragging" : undefined}
          sx={{
            ...CARDS_SECTION_TOP_OFFSET_SX,
            ...CARDS_ROW_SX,
            height: getCardsRowHeight(plans.length),
            ...(cardsRowScrollable && {
              cursor: isCardsRowDragging ? "grabbing" : "grab",
            }),
            ...(isCardsRowDragging && { userSelect: "none" }),
          }}
          role="list"
          aria-label="Assessment plans"
        >
          <Box
            role="button"
            tabIndex={0}
            aria-label="Add new plan"
            onClick={handleOpenCreate}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleOpenCreate();
              }
            }}
            sx={ADD_NEW_CARD_SX}
          >
            <AddIcon sx={{ fontSize: 24 }} />
            <Typography
              sx={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "20px",
                color: "inherit",
              }}
            >
              Add New
            </Typography>
          </Box>

          {plans.map((plan) => {
            const counts = getPlanCounts(plan);
            const isSelected = selectedPlanId === plan.id;
            const cardSx = {
              ...PLAN_CARD_BASE_SX,
              border: isSelected
                ? (theme) => `2px solid ${theme.palette.primary.main}`
                : "2px solid #E2E8F0",
            };
            return (
              <Box
                key={plan.id}
                role="listitem"
                tabIndex={0}
                onDragStart={(event) => event.preventDefault()}
                onClick={() => setSelectedPlanId(plan.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedPlanId(plan.id);
                  }
                }}
                sx={cardSx}
              >
                {/* Section 1+2: name+star and description (combined ~53px tall) */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    minHeight: 53,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      width: 280,
                      maxWidth: "100%",
                    }}
                  >
                    <Tooltip title={plan.name} placement="top">
                      <Typography sx={PLAN_CARD_NAME_SX}>{plan.name}</Typography>
                    </Tooltip>
                    <Tooltip
                      title={
                        plan.is_favorite
                          ? "Remove from favorites"
                          : "Add to favorites"
                      }
                    >
                      <IconButton
                        size="small"
                        aria-label={
                          plan.is_favorite ? "Remove from favorites" : "Add to favorites"
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleTogglePlanFavorite(plan);
                        }}
                        sx={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          p: 0.25,
                        }}
                      >
                        {plan.is_favorite ? (
                          <StarIcon
                            sx={{
                              width: 22,
                              height: 21.18,
                              color: "#F5C518",
                            }}
                          />
                        ) : (
                          <StarBorderIcon
                            sx={{
                              width: 22,
                              height: 21.18,
                              color: "#A6A6A6",
                            }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography
                    sx={{ ...PLAN_CARD_DESC_SX, width: 280, maxWidth: "100%" }}
                  >
                    {plan.description || "No description"}
                  </Typography>
                </Box>

                {/* Section 3: badges */}
                <Box
                  sx={{
                    display: "flex",
                    gap: "4px",
                    width: 280,
                    maxWidth: "100%",
                    height: 24,
                    flexWrap: "nowrap",
                    overflow: "hidden",
                    mt: 1,
                  }}
                >
                  <Chip
                    size="small"
                    label={`${counts.concepts} values`}
                    sx={PLAN_CARD_BADGE_SX}
                  />
                  <Chip
                    size="small"
                    label={`${counts.dimensions} dimension`}
                    sx={PLAN_CARD_BADGE_SX}
                  />
                  <Chip
                    size="small"
                    label={`${counts.keywords} keywords`}
                    sx={PLAN_CARD_BADGE_SX}
                  />
                </Box>

                {/* Section 4: divider */}
                <Box
                  sx={{
                    width: 280,
                    maxWidth: "100%",
                    height: 16,
                    py: "8px",
                    boxSizing: "border-box",
                  }}
                >
                  <Box sx={{ borderTop: "1.5px solid #F2F2F2" }} />
                </Box>

                {/* Section 5: date + edit/delete */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: 280,
                    maxWidth: "100%",
                    height: 20,
                  }}
                >
                  <Typography sx={PLAN_CARD_DATE_SX}>
                    {formatPlanDate(plan)} updated
                  </Typography>
                  <Box sx={{ display: "flex", gap: "4px" }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        sx={PLAN_CARD_ICON_BUTTON_SX}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenEdit(plan);
                        }}
                      >
                        <Box
                          component="img"
                          src={pencilIcon}
                          alt=""
                          sx={{ width: 16, height: 18, display: "block" }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={PLAN_CARD_ICON_BUTTON_SX}
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteTarget(plan);
                        }}
                      >
                        <Box
                          component="img"
                          src={trashIcon}
                          alt=""
                          sx={{ width: 16, height: 18, display: "block" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {selectedPlan && (
          <Box
            sx={{
              width: "100%",
              borderTop: "1px solid #F2F2F2",
            }}
          />
        )}

        {renderSelectedPlanDetails()}
      </>
    );
  };

  const formHeaderCopy = PLAN_FORM_HEADER_COPY[formMode];

  return (
    <>
      <Dialog
        open={open}
        onClose={withoutBackdropClose(onClose)}
        maxWidth={false}
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              width: 1200,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "92vh",
            },
          },
        }}
      >
        <DialogTitle component="div" sx={DIALOG_TITLE_ROW_SX}>
          <SelectPlanDialogIcon />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              alignSelf: "center",
            }}
          >
            <Typography component="span" sx={DIALOG_TITLE_TEXT_SX}>
              Select Impact Assessment Plan
            </Typography>
            <Typography component="span" sx={DIALOG_TITLE_DESC_SX}>
              Review dimension scope and select plan to run Assessment.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexShrink: 0,
              alignSelf: "center",
            }}
          >
            {footer && runRegulationIds && runRegulationIds.length > 0 ? (
              <Tooltip
                arrow
                placement="bottom"
                title={
                  <Box component="span" sx={{ display: "block" }}>
                    {runRegulationIds.map((regulationId) => (
                      <Typography
                        key={regulationId}
                        component="div"
                        variant="body2"
                        sx={{ lineHeight: 1.5, m: 0 }}
                      >
                        {regulationId}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <Box component="span" sx={RUN_FOR_REGULATIONS_BADGE_SX}>
                  <Typography component="span" sx={RUN_FOR_REGULATIONS_BADGE_TEXT_SX}>
                    {formatRunForRegulationsLabel(runRegulationIds.length)}
                  </Typography>
                </Box>
              </Tooltip>
            ) : null}
            <IconButton
              aria-label="Close"
              onClick={onClose}
              sx={DIALOG_CLOSE_BUTTON_SX}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={DIALOG_CONTENT_SX}>
          {renderPlanContent()}
        </DialogContent>
        {footer ? (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            {footer({ selectedPlan, loading, canReadChecklist })}
          </DialogActions>
        ) : null}
      </Dialog>

      <Dialog
        open={formOpen}
        onClose={withoutBackdropClose(handleCloseForm)}
        maxWidth={false}
        fullWidth
        slotProps={{
          paper: { sx: PLAN_FORM_DIALOG_PAPER_SX },
        }}
      >
        <DialogTitle component="div" sx={DIALOG_TITLE_ROW_SX}>
          <SelectPlanDialogIcon />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              alignSelf: "center",
            }}
          >
            <Typography component="span" sx={DIALOG_TITLE_TEXT_SX}>
              {formHeaderCopy.title}
            </Typography>
            <Typography component="span" sx={DIALOG_TITLE_DESC_SX}>
              {formHeaderCopy.description}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={handleCloseForm}
            sx={DIALOG_CLOSE_BUTTON_SX}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={PLAN_FORM_CONTENT_SX}>
          <ImpactAssessmentChecklistForm
            value={formValue}
            onChange={setFormValue}
            maxTotalSelections={maxTotalSelections}
            maxKeywordLength={maxKeywordLength}
            conceptsSelectorVariant="processMapping"
            descriptionPlaceholder="Brief description of this plan..."
          />
        </DialogContent>
        <DialogActions sx={PLAN_FORM_ACTIONS_SX}>
          <Button
            onClick={handleCloseForm}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontFamily: '"Nunito Sans", sans-serif',
              fontSize: 14,
              lineHeight: "20px",
              borderRadius: "4px",
              px: "12px",
              py: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!canSavePlan || saving}
            onClick={handleSavePlan}
            startIcon={<SaveIcon />}
            sx={{
              textTransform: "none",
              fontFamily: '"Nunito Sans", sans-serif',
              fontSize: 14,
              lineHeight: "20px",
              borderRadius: "4px",
              px: "12px",
              py: "8px",
              boxShadow: "none",
            }}
          >
            {saving ? "Saving…" : "Save plan"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={withoutBackdropClose(() => setDeleteTarget(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

